import { Typography } from '@euler/app/components/typography';
import { LoginBackButton } from '@euler/app/flows/auth/components/BackButton';
import { LoginHeader } from '@euler/app/flows/auth/components/LoginHeader';
import { LoginContent } from '@euler/app/flows/auth/components/LoginScreenView';
import { useAuthTheme, useLogin } from '@euler/app/flows/auth/hooks';
import { Button } from '@euler/components/Button';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  AuthProviderType,
  AuthenticatorNextActionApp,
} from '@euler/model/auth';
import { IdType } from '@euler/model/common';
import { useNavigation } from '@react-navigation/native';
import { memo, useState } from 'react';
import { FlatList, TouchableOpacity, View } from 'react-native';

function getDisplayInfo(app: AuthenticatorNextActionApp): {
  title: string;
  subTitle?: string;
} {
  if (app.orgName && app.storeName) {
    return { title: app.storeName, subTitle: app.orgName };
  }
  const raw = app.appTitle ?? app.appName;
  const dashIndex = raw.indexOf('-');
  if (dashIndex !== -1) {
    return {
      subTitle: raw.slice(0, dashIndex).trim(),
      title: raw.slice(dashIndex + 1).trim(),
    };
  }
  return { title: raw };
}

type AppSelectionItemProps = {
  item: AuthenticatorNextActionApp;
  isSelected: boolean;
  onPress: () => void;
};

const AppSelectionItem = memo(
  ({ item, isSelected, onPress }: AppSelectionItemProps) => {
    const theme = useAuthTheme();
    const { rpx } = useResponsiveContext();
    const { title, subTitle } = getDisplayInfo(item);

    return (
      <TouchableOpacity
        onPress={onPress}
        css={`
          padding: ${rpx(16)}px ${rpx(20)}px;
          margin-bottom: ${rpx(12)}px;
          background-color: ${theme.textInput.bgColor};
          border-radius: ${rpx(8)}px;
          border-width: 2px;
          border-color: ${isSelected ? theme.buttonBgColor : 'transparent'};
          flex-direction: row;
          align-items: center;
        `}
      >
        <View
          css={`
            flex: 1;
          `}
        >
          {subTitle ? (
            <Typography.Text
              light
              size={rpx(12)}
              color={theme.textInput.textColor}
              style={{ opacity: 0.5, marginBottom: rpx(4) }}
            >
              {subTitle}
            </Typography.Text>
          ) : null}
          <Typography.Text
            medium
            size={rpx(16)}
            color={theme.textInput.textColor}
          >
            {title}
          </Typography.Text>
        </View>
        {isSelected && (
          <Icon
            name="checkmark-circle"
            color={theme.buttonBgColor}
            size={rpx(22)}
          />
        )}
      </TouchableOpacity>
    );
  },
);

export const AppSelectionScreen = wrapNavigatorScreen(
  ({
    apps,
    authToken,
    onSelect,
  }: {
    apps: AuthenticatorNextActionApp[];
    authToken: string;
    onSelect?: (appId: IdType) => Promise<void> | void;
  }) => {
    const theme = useAuthTheme();
    const { rpx } = useResponsiveContext();
    const { isLoggingIn, login } = useLogin();
    const navigation = useNavigation();
    const canGoBack = navigation.canGoBack();
    const [selectedId, setSelectedId] = useState<IdType | null>(null);

    const handleLogin = () => {
      if (!selectedId) return;
      if (onSelect) {
        void Promise.resolve(onSelect(selectedId)).then(() => {
          if (navigation.canGoBack()) navigation.goBack();
        });
      } else {
        void login({
          type: AuthProviderType.TokenExchange,
          authToken,
          action: { type: 'app-targeting', appId: selectedId },
        });
      }
    };

    return (
      <>
        <View
          css={`
            flex: 1;
            background-color: ${theme.mainPanelBgColor};
          `}
        >
          <LoginContent>
            <View
              css={`
                flex: 1;
                padding: ${canGoBack ? rpx(80) : rpx(50)}px ${rpx(32)}px
                  ${rpx(32)}px;
              `}
            >
              <LoginHeader
                title="选择门店"
                subTitle="请选择要登录的门店然后继续"
              />
              <FlatList
                data={apps}
                keyExtractor={item => String(item.id)}
                css={`
                  flex: 1;
                `}
                renderItem={({ item }) => (
                  <AppSelectionItem
                    item={item}
                    isSelected={selectedId === item.id}
                    onPress={() => setSelectedId(item.id)}
                  />
                )}
              />
              <Button
                title="登录"
                onPress={handleLogin}
                disabled={!selectedId}
                loading={isLoggingIn}
                style={{ opacity: selectedId ? 1 : 0.4 }}
                textStyle={{
                  color: theme.buttonTextColor,
                  fontSize: rpx(18),
                  lineHeight: rpx(32),
                }}
                css={`
                  background-color: ${theme.buttonBgColor};
                  height: ${rpx(50)}px;
                  border-radius: ${rpx(25)}px;
                  margin-top: ${rpx(16)}px;
                `}
              />
            </View>
          </LoginContent>
        </View>
        {canGoBack && <LoginBackButton />}
      </>
    );
  },
  { headerShown: false },
);
